import { useQuery, useMutation } from '@tanstack/react-query';
import { DeliveryLogApi } from './api';
import type { DeliveryLogParams, SendTestSummaryRequest, SendTestReminderRequest, SimulateCommandRequest } from './types';

const QUERY_KEYS = {
  all: ['delivery-logs'] as const,
  list: (orgId: string, params?: DeliveryLogParams) =>
    [...QUERY_KEYS.all, orgId, 'list', params] as const,
  weeklyCounts: (orgId: string) => [...QUERY_KEYS.all, orgId, 'weekly-counts'] as const,
};

export const useDeliveryLogs = (orgId: string, params?: DeliveryLogParams) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(orgId, params),
    queryFn: () => DeliveryLogApi.getLogs(orgId, params),
    enabled: !!orgId,
  });
};

export const useWeeklyMessageCounts = (orgId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.weeklyCounts(orgId),
    queryFn: () => DeliveryLogApi.getWeeklyMessageCounts(orgId),
    enabled: !!orgId,
  });
};

export { QUERY_KEYS as deliveryQueryKeys };

export const useSendTestSummary = (orgId: string) => {
  return useMutation({
    mutationFn: (data: SendTestSummaryRequest) =>
      DeliveryLogApi.sendTestSummary(orgId, data),
  });
};

export const useSendTestReminder = (orgId: string) => {
  return useMutation({
    mutationFn: (data: SendTestReminderRequest) =>
      DeliveryLogApi.sendTestReminder(orgId, data),
  });
};

export const useSimulateCommand = (orgId: string) => {
  return useMutation({
    mutationFn: (data: SimulateCommandRequest) =>
      DeliveryLogApi.simulateCommand(orgId, data),
  });
};

export const useExportDeliveryLogs = (orgId: string) => {
  return useMutation({
    mutationFn: (params: Omit<DeliveryLogParams, 'page' | 'page_size'>) =>
      DeliveryLogApi.exportExcel(orgId, params),
  });
};
